import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bearingLabel, distanceLabel, loadLeaflet, towards } from '../../utils/geo';
import { drawNavigation } from '../map/MapCanvas';
import { ModeSwitch } from '../map/RoutePanel';
import SaveAreaButton from '../map/SaveAreaButton';
import { addBaseLayer } from '../../utils/offline/baseLayer';

/**
 * The way to the target, floating over the Field Mode camera (field
 * navigation, 2026-10-13).
 *
 * A small Leaflet map (about 140 px square) with the phone's position and its
 * accuracy, the target and the line between them, fitted to both, and under it
 * the distance and bearing ("120 m · NE 48°"); "You have arrived" within
 * ARRIVED_M, "No location" when the GPS has none. Collapsed, it is a pill with
 * the distance alone. Everything it needs comes in as props -- the camera's ONE
 * GPS watch (`fix`, `gpsState`), never a second -- and nothing in it can be
 * typed into: the camera stays a screen with nothing to type.
 *
 * The map is a picture, not a control: no dragging, zooming or keyboard, so a
 * thumb on it never moves anything. Tiles come from OpenStreetMap and are not
 * cached: offline the map is the dot, the ring and the line on blank, still
 * true. Where Leaflet itself cannot load (`loader` rejects, as in jsdom), the
 * caption alone says the way, and a line says the map could not be shown.
 *
 * Over a short screen on its side the map hides and the pill stays
 * (FieldCamera's LANDSCAPE_CSS hides `.fm-target-map-box`).
 */
//: Close enough to see the ground around the target, and no closer.
export const TARGET_ZOOM = 17;
//: However far the target, never zoomed out past a region.
export const MIN_ZOOM = 8;

export default function FieldTargetMap({ target, fix, gpsState, open, onToggle, loader = loadLeaflet,
  routeState = null, mode = null, onMode = null }) {
  const { t } = useTranslation(['field', 'route', 'common']);
  const route = routeState?.status === 'ok' ? routeState.route : null;
  const boxRef = useRef(null);
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const layersRef = useRef([]);
  const [mapState, setMapState] = useState('loading'); // 'loading' | 'ready' | 'failed'
  // Read when the map is made; a new function each render is not a new map.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  // The map, made when the overlay opens and removed when it closes.
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setMapState('loading');
    Promise.resolve().then(() => loaderRef.current()).then((L) => {
      if (!live) return;
      if (!L || typeof L.map !== 'function' || !boxRef.current) throw new Error('no leaflet');
      const map = L.map(boxRef.current, {
        zoomControl: false, attributionControl: true, dragging: false, touchZoom: false, scrollWheelZoom: false,
        doubleClickZoom: false, boxZoom: false, keyboard: false, tap: false, minZoom: MIN_ZOOM,
      });
      map.attributionControl?.setPrefix?.(false);
      addBaseLayer(L, map, { attribution: '© OpenStreetMap', maxZoom: 19 });
      mapRef.current = map;
      leafletRef.current = L;
      setMapState('ready');
    }).catch(() => { if (live) setMapState('failed'); });
    return () => {
      live = false;
      mapRef.current?.remove();
      mapRef.current = null;
      layersRef.current = [];
    };
  }, [open]);

  // The way, redrawn and refitted with every fix.
  const lat = fix?.lat;
  const lng = fix?.lng;
  const accuracy = fix?.accuracy_m;
  useEffect(() => {
    const L = leafletRef.current;
    const map = mapRef.current;
    if (mapState !== 'ready' || !L || !map || !target) return;
    const you = lat != null ? { lat, lng, accuracy_m: accuracy } : null;
    layersRef.current = drawNavigation(L, map, layersRef.current, { you, target, route });
    if (you) {
      map.fitBounds(L.latLngBounds([[lat, lng], [target.lat, target.lng]]), { padding: [18, 18], maxZoom: TARGET_ZOOM });
    } else {
      map.setView([target.lat, target.lng], TARGET_ZOOM);
    }
  }, [mapState, lat, lng, accuracy, target, route]);

  if (!target) return null;
  const way = towards(fix, target);
  let caption;
  let state;
  if (way?.arrived) {
    caption = t('camera.targetArrived');
    state = 'arrived';
  } else if (way) {
    caption = t('camera.targetDistance', { distance: distanceLabel(way.km, t), bearing: bearingLabel(way.bearing, t) });
    state = 'ok';
  } else {
    caption = gpsState === 'none' ? t('camera.targetNoGps') : t('camera.targetWaiting');
    state = gpsState === 'none' ? 'none' : 'waiting';
  }

  if (!open) {
    return (
      <div style={fs.root} data-testid="field-target-overlay" data-open="false">
        <button type="button" style={fs.pill} onClick={onToggle} aria-expanded="false" aria-controls="field-target-map"
          title={t('camera.targetShow')} aria-label={t('camera.targetShow')} data-testid="field-target-expand">
          <span aria-hidden="true">🎯 </span><bdi data-testid="field-target-caption" data-state={state}>{caption}</bdi>
        </button>
      </div>
    );
  }
  return (
    <div style={fs.root} data-testid="field-target-overlay" data-open="true" data-map={mapState}>
      <section id="field-target-map" role="region" aria-label={t('camera.targetMap')} style={fs.card}>
        <div className="fm-target-map-box" style={fs.mapBox} aria-hidden="true">
          <div ref={boxRef} style={fs.map} data-testid="field-target-leaflet" />
        </div>
        <p style={fs.caption} data-testid="field-target-caption" data-state={state}><bdi>{caption}</bdi></p>
        {route && (
          <p style={fs.note} data-testid="field-target-route"><bdi>{[
            route.drive && t('route:short.drive', { distance: distanceLabel(route.drive.metres / 1000, t) }),
            route.walk && t('route:short.walk', { distance: distanceLabel(route.walk.metres / 1000, t) }),
          ].filter(Boolean).join(' · ')}</bdi></p>
        )}
        {onMode && mode && (
          <ModeSwitch mode={mode} onChange={onMode} short testid="field-route-mode"
            style={{ marginTop: 4, width: '100%', justifyContent: 'space-between' }} />
        )}
        {onMode && (
          <div style={{ marginTop: 4 }}>
            <SaveAreaButton center={target} label={target.label || ''} compact testid="field-save-area" />
          </div>
        )}
        {mapState === 'failed' && (
          <p className="fm-target-map-box" style={fs.note} data-testid="field-target-map-failed">
            {t('camera.targetMapUnavailable')}
          </p>
        )}
        <button type="button" style={fs.collapse} onClick={onToggle} aria-expanded="true" aria-controls="field-target-map"
          title={t('camera.targetHide')} aria-label={t('camera.targetHide')} data-testid="field-target-collapse">
          <span aria-hidden="true">▾</span>
        </button>
      </section>
    </div>
  );
}

// Dark like the camera's chips, whatever the theme: it sits over a live picture.
const SIZE = 140;
const fs = {
  // A third layer of the camera, beside the top and the bottom: under the
  // chips, clear of the capture bar and its toast. `isolation` keeps Leaflet's
  // own z-indexes (400-1000) inside it.
  root: {
    position: 'absolute', zIndex: 2, isolation: 'isolate', insetInlineEnd: 12,
    top: 'calc(env(safe-area-inset-top) + 110px)', pointerEvents: 'auto', maxWidth: 'calc(100% - 24px)',
  },
  card: {
    position: 'relative', width: SIZE, padding: 4, borderRadius: 12, background: 'rgba(0, 0, 0, 0.72)',
    color: '#fff', boxShadow: '0 2px 10px rgba(0, 0, 0, 0.45)', margin: 0,
  },
  mapBox: { width: SIZE, height: SIZE, borderRadius: 9, overflow: 'hidden', background: '#dfe6dc' },
  map: { width: '100%', height: '100%' },
  caption: { margin: '4px 2px 0', fontSize: 13, fontWeight: 700, lineHeight: 1.3, textAlign: 'center' },
  note: { margin: '2px 2px 0', fontSize: 11, lineHeight: 1.3, textAlign: 'center', opacity: 0.85 },
  // A 44px target over the map's corner.
  collapse: {
    position: 'absolute', top: -8, insetInlineStart: -8, width: 44, height: 44, padding: 0, border: 'none',
    background: 'none', color: '#fff', font: 'inherit', fontSize: 16, cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  pill: {
    minHeight: 44, padding: '0 14px', borderRadius: 999, border: '1px solid rgba(255, 255, 255, 0.6)',
    background: 'rgba(0, 0, 0, 0.72)', color: '#fff', font: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
  },
};
